// UserManagementPage.js
import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react'; // Import React for memo
import { useFetch } from 'use-http';
import { UserContext } from '../App';
import {
    Box, Heading, Table, TableCell, TableHeader, TableRow,
    TextInput, Button, Text, FormField, CheckBox, Grid,
    Image, Spinner, Stack
} from 'grommet';
import { Trash, Edit, Checkmark, Close, Add, FormUp, FormDown } from 'grommet-icons';
import HomePageNav from "../components/Base components/HomePageNav";
import { getDBURL } from "../components/Utils/Utils";
import { ResponsiveContext } from 'grommet';
import DangerPopupWindow from "../components/Base components/DangerPopupWindow";
import { useErrorLogger } from '../components/Base components/Logger';

// ====================================================================================
// This is a memoized React component responsible for rendering a single row in the user management table. It has two states:
// Display Mode: Shows the user's email, name, and role, along with "Edit" and "Delete" buttons.
// Editing Mode: Replaces the user's name and role with input fields and shows "Save" and "Cancel" buttons.
// Using React.memo is a performance optimization that prevents the row from re-rendering unless the specific data for that user changes.
// ====================================================================================
const UserRow = React.memo(({
    user,
    isEditing,
    editingDisplayName,
    editingIsAdmin,
    onStartEditing,
    onCancelEditing,
    onSaveChanges,
    onOpenDangerPopup,
    onUpdateField,
}) => {
    return (
        <TableRow key={user.uid}>
            <TableCell>{user.email}</TableCell>
            <TableCell background={isEditing ? 'light-2' : undefined}>
                {isEditing ? (
                    <TextInput plain value={editingDisplayName} onChange={e => onUpdateField('displayName', e.target.value)} />
                ) : (
                    user.displayName
                )}
            </TableCell>
            <TableCell background={isEditing ? 'light-2' : undefined}>
                <CheckBox
                    checked={isEditing ? editingIsAdmin : user.isAdmin}
                    disabled={!isEditing}
                    onChange={() => onUpdateField('isAdmin', !editingIsAdmin)}
                    label={user.isAdmin ? "Admin" : "User"}
                />
            </TableCell>
            <TableCell>
                {isEditing ? (
                    <Box direction="row" gap="small">
                        <Button secondary style={{ background: "transparent" }} icon={<Checkmark color="status-ok" size='16px' />} onClick={() => onSaveChanges(user.uid)} tip="Save" />
                        <Button secondary style={{ background: "transparent" }} icon={<Close color="status-critical" size='16px' />} onClick={onCancelEditing} tip="Cancel" />
                    </Box>
                ) : (
                    <Box direction="row" gap="small">
                        <Button secondary style={{ background: "transparent" }} icon={<Edit size='16px' />} onClick={() => onStartEditing(user)} tip="Edit user" />
                        <Button secondary style={{ background: "transparent" }} icon={<Trash color="status-critical" size='16px' />} onClick={() => onOpenDangerPopup(user)} tip="Delete user" />
                    </Box>
                )}
            </TableCell>
        </TableRow>
    );
});


export default function UserManagement() {
    const [users, setUsers] = useState([]);
    const { get, post } = useFetch(getDBURL());

    // State for the "Create User" form
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    // State for editing a user in the table
    const [editingUid, setEditingUid] = useState(null); // Tracks which user is being edited
    const [editingDisplayName, setEditingDisplayName] = useState(''); // Holds the new display name value during edit
    const [editingIsAdmin, setEditingIsAdmin] = useState(false);

    // user for deleting:
    const [deletingUser, setDeletingUser] = useState(null);

    // General UI state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showDangerPopup, setShowDangerPopup] = useState(false);

    // State to manage sorting configuration.
    const [sort, setSort] = useState({ column: 'email', direction: 'asc' });

    // Get the profile from the context. We already know the user is an admin
    // because the router would not have rendered this component otherwise.
    const currentUser = useContext(UserContext);

    // Initialize the action logger hook
    const { DBlogError } = useErrorLogger();
    const size = useContext(ResponsiveContext);

    const clearForm = () => {
        setEmail('');
        setPassword('');
        setDisplayName('');
        setIsAdmin(false);
    };

    useEffect(() => {
        const fetchUsers = async () => {
            setIsLoading(true);
            try {
                if (!currentUser?.isAdmin) return;
                const timestamp = Date.now();
                const response = await get(`/users/dt=${timestamp}`);
                if (!response.success) {
                    throw new Error(response.message || 'An unknown error occurred.');
                }
                setUsers(response.result || []);
            } catch (err) {
                console.error('Failed to fetch users', err);
                setError('Failed to fetch users.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchUsers();
    }, []);

    // ====================================================================================
    // The function is wrapped in useCallback for performance. This ensures the function isn't recreated on every render,
    // which helps prevent unnecessary re-rendering of child components that might use it as a prop.
    // ====================================================================================
    const handleCreate = useCallback(async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        if (email.trim().length > 50) {
            setError("Email address cannot be longer than 50 characters.");
            return;
        }
        setIsLoading(true);
        try {
            const newUserPayload = { email: email.trim(), password, isAdmin, displayName: displayName.trim() };
            const response = await post('/create-user', newUserPayload);
            if (!response.success) throw new Error(response.message || 'An unknown error occurred.');
            const createdUser = response.result;
            setSuccessMessage(`User ${createdUser.email} successfully created!`);
            setUsers(prevUsers => [...prevUsers, createdUser]);
            await DBlogError({ userName: currentUser.email, moduleName: 'UserManagement', functionName: 'CREATE_USER', parameters: { createdUserEmail: createdUser.email, displayName: createdUser.displayName, assignedAdminStatus: isAdmin }, description: `User ${createdUser.email} created successfully!` });
            clearForm();
        } catch (err) {
            const userFriendlyMessage = err.message || "Unable to create user account.";
            setError(userFriendlyMessage);
            await DBlogError({ err, parameters: { email: email.trim(), isAdmin, displayName: displayName.trim() }, userName: currentUser.email, moduleName: 'UserManagement', functionName: 'handleCreate', description: `Failed to create user. Reason: ${err.message}` });
        } finally {
            setIsLoading(false);
        }
    }, [email, password, displayName, isAdmin, currentUser, DBlogError, post]);

    const handleSaveChanges = useCallback(async (uid) => {
        setIsLoading(true);
        setError('');
        setSuccessMessage('');
        const originalUser = users.find(u => u.uid === uid);
        try {
            const updatedUserData = { uid, displayName: editingDisplayName.trim(), isAdmin: editingIsAdmin };
            const response = await post('/update-user', updatedUserData);
            if (!response.success) throw new Error(response.message || 'An unknown error occurred.');
            setSuccessMessage(`User ${originalUser.email} updated successfully!`);
            setUsers(prevUsers => prevUsers.map(user => user.uid === uid ? { ...user, displayName: editingDisplayName.trim(), isAdmin: editingIsAdmin } : user));
            setEditingUid(null);
            await DBlogError({ userName: currentUser.email, moduleName: 'UserManagement', functionName: 'UPDATE_USER', parameters: { targetUserEmail: originalUser.email, updatedDisplayName: editingDisplayName, updatedAdminStatus: editingIsAdmin }, description: 'User updated successfully!' });
        } catch (err) {
            setError(err.message);
            await DBlogError({ err, parameters: { targetUserEmail: originalUser.email, updatedDisplayName: editingDisplayName, updatedAdminStatus: editingIsAdmin }, userName: currentUser.email, moduleName: 'UserManagement', functionName: 'UPDATE_USER' });
        } finally {
            setIsLoading(false);
        }
    }, [users, editingDisplayName, editingIsAdmin, currentUser, DBlogError, post]);

    const handleDeleteUser = useCallback(async (userToDelete) => {
        setIsLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            const delUser = { email: userToDelete.email };
            const response = await post('/delete-user', delUser);
            if (!response.success) throw new Error(response.message || "An unknown error occurred.");
            setSuccessMessage(`User ${userToDelete.email} deleted.`);
            setUsers(prevUsers => prevUsers.filter(u => u.uid !== userToDelete.uid));
            await DBlogError({ userName: currentUser.email, moduleName: 'UserManagement', functionName: 'DELETE_USER', parameters: { deletedUserEmail: userToDelete.email }, description: `User ${userToDelete.email} deleted successfully!` });
        } catch (err) {
            setError(err.message);
            await DBlogError({ err, parameters: { deletedUserEmail: userToDelete.email }, userName: currentUser.email, moduleName: 'UserManagement', functionName: 'DELETE_USER' });
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, DBlogError, post]);

    const onDangerConfirm = async () => {
        setShowDangerPopup(false);
        await handleDeleteUser(deletingUser);
    };

    const openDangerPopupWindow = useCallback((selectedUser) => {
        setDeletingUser(selectedUser);
        setShowDangerPopup(true);
    }, []);

    const startEditing = useCallback((user) => {
        setEditingUid(user.uid);
        setEditingDisplayName(user.displayName || '');
        setEditingIsAdmin(user.isAdmin);
    }, []);

    const cancelEditing = useCallback(() => {
        setEditingUid(null);
    }, []);

    const handleSort = useCallback((columnProperty) => {
        setSort(currentSort => {
            const isAsc = currentSort.column === columnProperty && currentSort.direction === 'asc';
            return { column: columnProperty, direction: isAsc ? 'desc' : 'asc' };
        });
    }, []);

    // Add a memoized handler to update fields in edit mode
    // This simplifies the props passed to UserRow.
    const handleUpdateFieldInEditMode = useCallback((field, value) => {
        if (field === 'displayName') {
            setEditingDisplayName(value);
        } else if (field === 'isAdmin') {
            setEditingIsAdmin(value);
        }
    }, []);

    const sortedUsers = useMemo(() => {
        const { column, direction } = sort;
        return [...users].sort((a, b) => {
            const aVal = a[column];
            const bVal = b[column];
            let comparison = 0;
            if (typeof aVal === 'string') {
                comparison = aVal.localeCompare(bVal);
            } else if (typeof aVal === 'boolean') {
                comparison = Number(aVal) - Number(bVal);
            }
            return direction === 'asc' ? comparison : -comparison;
        });
    }, [users, sort]);

    if (!currentUser?.isAdmin) {
        return <Box pad="medium"><Text color="status-critical">You do not have permission to view this page.</Text></Box>;
    }
    
    // ... All JSX for forms and headers remains the same ...
    const isSmall = size === 'small' || size === 'xsmall';

    const columns = [
        { property: 'email', header: 'Email', size: 'medium' },
        { property: 'displayName', header: 'Name', size: 'small' },
        { property: 'isAdmin', header: 'Role', size: 'small' },
        { property: 'actions', header: 'Actions', size: 'small' }
    ];

    const createUserForm = (
        <Box >
            <Heading level={4} margin="none" >Add New User</Heading>
            <form autoComplete="off">
                {/* RESPONSIVE LAYOUT: Grid for large screens, Box for small */}
                {isSmall ? (
                    <Box gap="medium" margin={{ bottom: "large" }}  width="70vw">
                    <Box overflow={{ vertical: "auto", horizontal: "hidden" }}>
                        <FormField margin={{ vertical: "medium" }} required>
                        <Text  margin={{ vertical: "xxsmall" }}>Email</Text>
                            <TextInput
                                type="email"
                                name="new-user-email"
                                autoComplete="off"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter user email"                            
                            />
                        </FormField>
                        
                        <FormField margin={{ vertical: "medium" }} required>
                        <Text  margin={{ vertical: "xxsmall" }}>User name</Text>
                            <TextInput
                                name="new-user-displayName"
                                autoComplete="off"
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                placeholder="Enter user name"
                            />
                        </FormField>

                        <FormField margin={{ vertical: "medium" }} required>
                            <Text  margin={{ vertical: "xxsmall" }}>Password</Text>
                            <TextInput
                                type="password"
                                name="new-user-password"
                                autoComplete="new-password"
                                value={password}
                                onChange={e => setPassword(e.target.value.trim())}
                                placeholder="Minimum 6 characters"
                            />
                        </FormField>
                        <FormField>
                            <CheckBox label="Admin" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
                        </FormField>
                        <Box margin={{ vertical: "large", horizontal: "auto" }}>
                            <Button primary label="Add User" onClick={handleCreate} disabled={isLoading} />
                        </Box>
                    </Box>
                    </Box>
                ) : (
                    <Grid columns={columns.map(c => c.size)} gap="medium" align="end">
                        <FormField margin={{ vertical: "medium" }} required>
                            <Text  margin={{ vertical: "xxsmall" }}>Email</Text>
                            <TextInput
                                type="email"
                                name="new-user-email"
                                autoComplete="off"                                        
                                value={email}
                                onChange={(e) => setEmail(e.target.value.trim())}
                                placeholder="Enter user email"
                            />
                        </FormField>

                        <FormField margin={{ vertical: "medium" }} required>
                        <Text  margin={{ vertical: "xxsmall" }}>User name</Text>
                            <TextInput
                                name="new-user-displayName"
                                autoComplete="off"                                        
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                placeholder="Enter user name"
                            />
                          </FormField>
                          <FormField margin={{ vertical: "medium" }} required>
                          <Text  margin={{ vertical: "xxsmall" }}>Password</Text>
                              <TextInput
                                  type="password"
                                  name="new-user-password"
                                  autoComplete="new-password"                                          
                                  value={password}
                                  onChange={e => setPassword(e.target.value.trim())}
                                  placeholder="Minimum 6 characters"
                              />
                          </FormField>

                          <Box direction="row" gap="medium" align="center" margin={{ vertical: "medium" }} >
                              <CheckBox label="Admin" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
                              <Button icon={<Add />} tip="Add User" onClick={handleCreate} primary disabled={isLoading} />
                          </Box>
                    </Grid>
                )}
            </form>
        </Box>
    );

    // ====================================================================================
    // Update the table body to use the new `UserRow` component
    // We now map over the data and render our memoized component, passing the required
    // state and the new memoized handlers as props.
    // ====================================================================================
    const tableBodyContent = sortedUsers.length === 0 ? (
        <TableRow>
            <TableCell colSpan={4}>
                <Box align="center" pad="large" gap="small">
                    <Text>No users found in the system.</Text>
                    <Text size="small" color="text-weak">Try creating a new user above.</Text>
                </Box>
            </TableCell>
        </TableRow>
    ) : (
        sortedUsers.map(user => (
            <UserRow
                key={user.uid}
                user={user}
                isEditing={editingUid === user.uid}
                editingDisplayName={editingUid === user.uid ? editingDisplayName : ''}
                editingIsAdmin={editingUid === user.uid ? editingIsAdmin : false}
                onStartEditing={startEditing}
                onCancelEditing={cancelEditing}
                onSaveChanges={handleSaveChanges}
                onOpenDangerPopup={openDangerPopupWindow}
                onUpdateField={handleUpdateFieldInEditMode}
            />
        ))
    );

    const usersTable = (
        <Table>
            <TableHeader>
                <TableRow>
                    {columns.map(c => (
                        <TableCell key={c.property} scope="col" size={c.size}>
                            <Button plain onClick={() => handleSort(c.property)}>
                                <Box direction="row" align="center" gap="xsmall">
                                    <Text weight="bold">{c.header}</Text>
                                    {sort.column === c.property && (
                                        sort.direction === 'asc' ? <FormDown size="small" /> : <FormUp size="small" />
                                    )}
                                </Box>
                            </Button>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHeader>
            <tbody>{tableBodyContent}</tbody>
        </Table>
    );

    // ====================================================================================
    // We will only show the large, centered spinner on the very first load when there
    // are no users to display. For other loading states (like saving/creating), the
    // `disabled={isLoading}` on buttons provides sufficient feedback without disrupting
    // the whole page layout.
    // ====================================================================================
    return (
        <Box width="90vw" height="full" background="light-1" margin="0 auto" overflow={{vertical: "auto", horizontal: "hidden"}}  >
            <Box
                style={{ 
                    position: 'fixed',
                    top: 0,
                    zIndex: 10,
                }}>                
                <HomePageNav 
                    isDefault={true}
                />
            </Box>

            <Box>
                <Box style={{ height:"40px", minHeight:"40px"}}>
                    <Box style={{position:"relative"}}>
                        <Box background="neutral-3" pad={{vertical: "small"}} margin={{vertical: "xxsmall"}} >
                            <Box direction="row" justify="center" align="center">
                                <Text size="large" weight="bold" color="#ffffff" > User Management </Text>
                            </Box>              
                        </Box>  
                        <Box height="xsmall" width="xsmall"  
                            style={{position:"absolute", right: "3%", top: "15px"}}
                            right="0px"
                            >
                                <Image
                                    size="contain"
                                    src="Grape.png" 
                                    width={size === "small" ? "70%" : size === "medium" ? "100%" : "150%"}                                     
                                    height={size === "small" ? "70%" : size === "medium" ? "100%" : "150%"}                                    
                                />
                        </Box>                         
                    </Box>      
                </Box>
            </Box>

            {showDangerPopup && (
            <DangerPopupWindow
                msg={`Are you sure you want to delete ${deletingUser.email}?`}
                onClose={()=>setShowDangerPopup(false)}
                onConfirm={onDangerConfirm}
            />)}

            <Stack guidingChild="first" fill>
            <Box align="center"  pad="medium" gap="medium">
                
                   {isLoading  && users.length === 0 ? (
                        // If loading, show a centered spinner. The page layout is already stable.
                    <Box justify="center" align="center" pad="large" gap="medium" fill>
                        <Spinner size="large" />
                        <Text>Loading Users...</Text>
                    </Box>
                ) : (
                    <Box  pad={{ top: 'large' }}>                
                        <Heading level={2} margin="0 auto" color="brand" >Users Data</Heading>

                        <Box>
                            {/* Global messages */}
                            <Box height="50px" justify="center">
                            {(error || successMessage) && (
                                <Box margin='medium'>
                                    {error && <Text color="status-critical">{error}</Text>}
                                    {successMessage && <Text color="status-ok">{successMessage}</Text>}
                                </Box>
                            )}
                            </Box>
                        </Box>
                
                        <Box margin="0 auto">
                            <Box pad="medium" gap="medium" >
                                <Box overflow={{ horizontal: 'auto' }} >
                                    <Box style={{ minWidth: '900px'}} gap="medium">
                                        {/* --- Create User Form --- */}
                                        {createUserForm}

                                        <Box border={{ side: 'bottom', color: 'border-weak' }} />

                                        {/* --- Users Table --- */}
                                        {usersTable}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

            </Box>
            </Stack>
        </Box>
    );
}